/**
 * Public contract signing page — /doserne/kontrakt/:token
 *
 * No login required. First asks the employee to pick a language (when the
 * contract exists in more than one), then renders the WHOLE page + the
 * inline contract in that language and lets them sign with a finger (or
 * typed name). The signed PDF is emailed to them.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, CheckCircle2, Type, Brush, PenLine } from "lucide-react";
import SignaturePad from "../apps/shared/SignaturePad";
import PdfViewerModal from "./PdfViewerModal";
import LangFlag from "./LangFlag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  no: { load: "Laster kontrakt…", invalid: "Ugyldig eller utløpt lenke.", title: "Kontrakt til signering",
    read: "Les kontrakten, og signer nederst på siden.", draw: "Tegn signatur", type: "Skriv navnet ditt",
    typed_ph: "Skriv fullt navn", ack: "Jeg har lest og godtar kontrakten i sin helhet.",
    sign: "Signer kontrakten", signed_title: "Kontrakt signert ✓", signed_sub: "Takk! En kopi er sendt til e-posten din.",
    download: "Last ned signert PDF", err: "Kunne ikke signere", scroll_hint: "Bla ned for å signere ↓",
    choose_lang: "Velg språk", choose_lang_sub: "Velg språket du vil lese og signere kontrakten på.", view_signed: "Vis signert PDF", close: "Lukk" },
  ro: { load: "Se încarcă contractul…", invalid: "Link invalid sau expirat.", title: "Contract de semnat",
    read: "Citește contractul și semnează la finalul paginii.", draw: "Desenează semnătura", type: "Scrie numele",
    typed_ph: "Scrie numele complet", ack: "Am citit și accept contractul în întregime.",
    sign: "Semnează contractul", signed_title: "Contract semnat ✓", signed_sub: "Mulțumim! O copie a fost trimisă pe email.",
    download: "Descarcă PDF semnat", err: "Nu s-a putut semna", scroll_hint: "Derulează în jos pentru a semna ↓",
    choose_lang: "Alege limba", choose_lang_sub: "Alege limba în care vrei să citești și să semnezi contractul.", view_signed: "Vezi PDF semnat", close: "Închide" },
  en: { load: "Loading contract…", invalid: "Invalid or expired link.", title: "Contract to sign",
    read: "Read the contract and sign at the bottom of the page.", draw: "Draw signature", type: "Type your name",
    typed_ph: "Type full name", ack: "I have read and accept the contract in full.",
    sign: "Sign the contract", signed_title: "Contract signed ✓", signed_sub: "Thanks! A copy has been sent to your email.",
    download: "Download signed PDF", err: "Could not sign", scroll_hint: "Scroll down to sign ↓",
    choose_lang: "Choose language", choose_lang_sub: "Choose the language to read and sign the contract in.", view_signed: "View signed PDF", close: "Close" },
  pl: { load: "Ładowanie umowy…", invalid: "Nieprawidłowy lub wygasły link.", title: "Umowa do podpisania",
    read: "Przeczytaj umowę i podpisz na dole strony.", draw: "Narysuj podpis", type: "Wpisz imię",
    typed_ph: "Wpisz pełne imię i nazwisko", ack: "Przeczytałem i akceptuję umowę w całości.",
    sign: "Podpisz umowę", signed_title: "Umowa podpisana ✓", signed_sub: "Dziękujemy! Kopia została wysłana na e-mail.",
    download: "Pobierz podpisany PDF", err: "Nie udało się podpisać", scroll_hint: "Przewiń w dół, aby podpisać ↓",
    choose_lang: "Wybierz język", choose_lang_sub: "Wybierz język, w którym chcesz przeczytać i podpisać umowę.", view_signed: "Zobacz podpisany PDF", close: "Zamknij" },
};

const LANG_NAMES = { no: "Norsk", ro: "Română", en: "English", pl: "Polski" };

export default function DoserneContractSignPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [mode, setMode] = useState("drawn");
  const [drawn, setDrawn] = useState(null);
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageLang, setPageLang] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const htmlByLang = data?.html_by_lang || {};
  const availableLangs = (data?.langs && data.langs.length ? data.langs : Object.keys(htmlByLang)).filter((l) => htmlByLang[l]);
  const signed = !!data?.signed;

  // Language the whole page + inline contract renders in. When only one is
  // available (or none), fall back to it / the member's default automatically.
  const effectiveLang =
    pageLang
    || (availableLangs.length === 1 ? availableLangs[0] : null)
    || (data?.lang && T[data.lang] ? data.lang : "no");
  const t = T[effectiveLang] || T.no;

  // Force a language pick before showing the document (multi-language + unsigned).
  const needsLangChoice = !signed && !pageLang && availableLangs.length > 1;

  const contractHtml = htmlByLang[effectiveLang] || htmlByLang[availableLangs[0]] || "";
  const hasInline = !!contractHtml;

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/doserne/contracts/public/${token}`);
      setData(res.data);
    } catch { setInvalid(true); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const sign = async () => {
    if (!ack) { toast.error(t.ack); return; }
    if (mode === "drawn" && !drawn) { toast.error(t.draw); return; }
    if (mode === "typed" && typed.trim().length < 2) { toast.error(t.typed_ph); return; }
    setSaving(true);
    try {
      const payload = mode === "drawn"
        ? { signature: drawn, signature_type: "drawn", lang: effectiveLang }
        : { signature_text: typed.trim(), signature_type: "typed", lang: effectiveLang };
      await axios.post(`${API}/doserne/contracts/public/${token}/sign`, payload);
      toast.success(t.signed_title);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t.err); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (invalid || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafaf7] p-6" data-testid="contract-public-invalid">
      <div className="text-center"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-600 font-semibold">{t.invalid}</p></div>
    </div>
  );

  // ---- STEP 1: language selection (multi-language, unsigned) ----
  if (needsLangChoice) return (
    <div className="min-h-screen bg-[#fafaf7] flex items-center justify-center py-8 px-4" data-testid="contract-public-lang-step">
      <div className="max-w-md w-full">
        <div className="bg-slate-900 text-white rounded-2xl p-5 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Doserne</div>
          <h1 className="text-xl font-black">{data.name || ""}</h1>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="text-lg font-black text-slate-900">Velg språk · Alege limba</h2>
          <p className="text-sm text-slate-500 mt-1">Choose the language to read and sign the contract in.</p>
          <div className="grid grid-cols-1 gap-2 mt-4">
            {availableLangs.map((lg) => (
              <button key={lg} type="button" onClick={() => setPageLang(lg)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-slate-200 hover:border-slate-900 hover:bg-slate-50 text-left transition-colors"
                data-testid={`contract-lang-choice-${lg}`}>
                <LangFlag lang={lg} size={28} />
                <span className="text-sm font-black text-slate-900">{LANG_NAMES[lg] || lg.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ---- STEP 2: read + sign (rendered fully in the chosen language) ----
  return (
    <div className="min-h-screen bg-[#fafaf7] py-8 px-4" data-testid="contract-public-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-900 text-white rounded-2xl p-5 mb-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Doserne</div>
              <h1 className="text-xl font-black">{signed ? t.signed_title : t.title}</h1>
              {data.name && <div className="text-sm text-slate-300 mt-0.5">{data.name}</div>}
            </div>
            <span className="flex-shrink-0" title={LANG_NAMES[effectiveLang]}><LangFlag lang={effectiveLang} size={26} /></span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
          <p className="text-sm text-slate-600">{signed ? t.signed_sub : t.read}</p>

          {/* Inline contract (employer already signed inside) — before signing */}
          {hasInline && !signed && (
            <>
              <iframe
                title="contract"
                srcDoc={contractHtml}
                className="w-full mt-3 rounded-xl border border-slate-200 bg-white"
                style={{ height: "60vh" }}
                data-testid="contract-inline-view"
              />
              <p className="text-xs text-slate-400 mt-3 text-center font-semibold">{t.scroll_hint}</p>
            </>
          )}

          {/* After signing — offer to view/download the signed PDF */}
          {signed && (
            <button type="button" onClick={() => setPdfOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold"
              data-testid="contract-public-view-signed">
              <FileText className="w-4 h-4" /> {t.view_signed}
            </button>
          )}
        </div>

        {!signed && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="contract-public-sign-block">
            <div className="flex items-center gap-2"><PenLine className="w-4 h-4 text-slate-500" /><span className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Signer</span></div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMode("drawn")}
                className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border-2 ${mode === "drawn" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500"}`}
                data-testid="pub-mode-drawn"><Brush className="w-4 h-4" /> {t.draw}</button>
              <button type="button" onClick={() => setMode("typed")}
                className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border-2 ${mode === "typed" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500"}`}
                data-testid="pub-mode-typed"><Type className="w-4 h-4" /> {t.type}</button>
            </div>
            {mode === "drawn" ? (
              <SignaturePad value={drawn} onChange={setDrawn} height={160} label={t.draw} />
            ) : (
              <input type="text" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t.typed_ph}
                className="w-full px-4 py-4 rounded-lg border-2 border-slate-200 text-slate-900 bg-white text-2xl focus:outline-none focus:border-slate-900"
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }} data-testid="pub-typed-input" />
            )}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1 w-4 h-4" data-testid="pub-ack" />
              <span className="text-sm text-slate-700 leading-snug">{t.ack}</span>
            </label>
            <button type="button" onClick={sign} disabled={saving || !ack || (mode === "drawn" ? !drawn : typed.trim().length < 2)}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-black uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 text-slate-900 disabled:bg-slate-200 disabled:text-slate-400"
              data-testid="pub-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {t.sign}
            </button>
          </div>
        )}
      </div>

      {pdfOpen && (
        <PdfViewerModal
          url={`${API}/doserne/contracts/public/${token}/pdf?signed=1`}
          downloadUrl={`${API}/doserne/contracts/public/${token}/pdf?signed=1&dl=1`}
          filename={`Kontrakt_${(data.name || "ansatt").replace(/\s+/g, "_")}.pdf`}
          title={t.title}
          labels={{ download: t.download, close: t.close }}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}
